/**
 * Text banks and combinatorial patterns for Divergent Self LifeRecord generation.
 * Recognizable world with Earth's physics and mostly familiar society where
 * concrete historical forks and personal decisions led to a distinct life.
 */

import type { EarthDimension } from "../../types";

export interface HistoricalFork {
  id: string; // e.g. "d1", "d2"
  title: string;
  divergenceText: string;
  divergenceEffects: string;
  societySummary: string;
  laws: string;
  culture: string;
  institutions: string;
  expectations: string;
  opportunity: string;
  technology: string;
  science: string;
  geography: string;
  history: string;
  environment: string;
  howDiffersHeadline: string;
  howDiffersParagraphs: [string, string, string];
  keyDifferences: [
    { title: string; text: string },
    { title: string; text: string },
    { title: string; text: string },
    { title: string; text: string }
  ];
  earthDimensionBecause: Record<EarthDimension, { here: string; earth: string; because: string }>;
}

export const DIVERGENT_HISTORICAL_FORKS: readonly HistoricalFork[] = [
  {
    id: "d1",
    title: "Regional Guild Charter & Anti-Monopoly Reform",
    divergenceText: "A landmark mid-century industrial decentralization charter prohibited cross-basin financial monopolies and established self-governing regional craft guilds with veto power over regional automation.",
    divergenceEffects: "Preserves durable craftsmanship, ensures long-term vocational tenures, and tethers enterprise capital directly to watershed-level civic accountability.",
    societySummary: "A decentralized municipal federation where chartered trade guilds collaborate directly with elected district councils.",
    laws: "Statutory protections against corporate asset stripping, mandatory open-hardware repair manuals, and civic commons covenants.",
    culture: "Values generational competence, repaired tools, visible handiwork, and unhurried civic assemblies over speculative speed.",
    institutions: "District craft guilds, public material trusts, municipal technical boards, and mutualist credit associations.",
    expectations: "Citizens are expected to master a tangible vocational trade and dedicate regular hours to public maintenance boards.",
    opportunity: "Open and meritocratic within regional guild ladders, prioritizing demonstrable skill and peer trust over financial pedigree.",
    technology: "Near-contemporary precision manufacturing, repairable modular electronics, and localized clean microgrids built for fifty-year service lifetimes.",
    science: "Empirical thermodynamics, materials science, hydrology, and municipal resilience modeling with open peer registries.",
    geography: "Prosperous river corridors and market townships interwoven with protected agricultural reserves and high-speed freight rail.",
    history: "Seven decades of steady municipal development following the peaceful dissolution of centralized speculative trusts in the late 1950s.",
    environment: "Thriving river basins and restorative forest buffers maintained through cooperative land agreements and zero toxic effluent mandates.",
    howDiffersHeadline: "An economy organized around durable physical mastery rather than speculative financial abstraction.",
    howDiffersParagraphs: [
      "Where contemporary Earth allowed global capital to offshore production and mandate planned obsolescence, this reality established legally binding guild charters that hold manufacturers directly liable for the repairability and longevity of every device.",
      "Higher education here is seamlessly woven into municipal technical workshops rather than segregated onto speculative university campuses, graduating practitioners who carry zero institutional debt and hold proven authority by age twenty-five.",
      "Civic status is measured by verified contributions to public infrastructure—such as bridges, aquifers, and power cooperatives—rather than media visibility, personal stock portfolios, or corporate executive ranks."
    ],
    keyDifferences: [
      {
        title: "Perpetual Right-to-Repair Charters",
        text: "Every piece of industrial and domestic machinery must carry stamped schematics, standard fasteners, and fifty-year spare-part guarantees."
      },
      {
        title: "Basin-Level Mutualist Banking",
        text: "Credit is issued interest-free by municipal assemblies strictly for productive local infrastructure and cooperative enterprises."
      },
      {
        title: "Guild-Supervised Technology Adoption",
        text: "Automation is deployed to eliminate dangerous manual toil, with efficiency dividends legally distributed across all guild members."
      },
      {
        title: "Decentralized Energy Cooperatives",
        text: "Power grids are owned and calibrated at the county scale, balancing seasonal hydro, solar, and biomass without corporate intermediaries."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Chartered guild practitioner with guaranteed workshop tenure and civic standing",
        earth: "At-will corporate specialist or precarious gig contractor",
        because: "Guild charters prevent unilateral downsizing and guarantee practitioners institutional ownership of their trades."
      },
      "Social status": {
        here: "Recognized master artisan and voting trustee on district planning boards",
        earth: "Middle-tier service worker evaluated by corporate performance metrics",
        because: "Social hierarchy is tied directly to verified public craft competence rather than financial rents."
      },
      "Financial circumstances": {
        here: "Debt-free equity in cooperative workshops and mutualist dividend credits",
        earth: "Asset-light wage earner vulnerable to housing inflation and consumer credit debt",
        because: "Basin-level mutualist banking eliminated predatory interest and speculative housing bubbles."
      },
      "Relationships": {
        here: "Intergenerational guild networks and lifelong neighborhood collaboration",
        earth: "Geographically dispersed connections mediated primarily by digital platforms",
        because: "Long-term regional rootedness allows personal ties to mature across decades of shared physical projects."
      },
      "Freedoms": {
        here: "Substantial operational autonomy within the workshop paired with civic duty",
        earth: "Broad consumer market options constrained by algorithmic workplace monitoring",
        because: "Workplaces are democratically operated, while governance emphasizes active civic participation."
      },
      "Daily life": {
        here: "Tactile, rhythm-driven cadence alternating between physical craft and community consultation",
        earth: "Screen-dominated sedentary workday punctuated by automated notifications",
        because: "Technology serves to support human handiwork rather than replacing physical human agency."
      },
      "Available technology": {
        here: "Robust, modular precision instruments designed for lifetime maintenance",
        earth: "Sleek proprietary consumer electronics built with planned obsolescence",
        because: "Statutory mandates outlaw unrepairable sealed assemblies and software locks."
      },
      "Future opportunities": {
        here: "Predictable progression toward guild leadership and municipal advisory councils",
        earth: "Volatile career trajectories exposed to corporate reorganizations and algorithmic shifts",
        because: "Civic institutions cultivate generational stability rather than chasing quarterly shareholder disruption."
      }
    }
  },
  {
    id: "d2",
    title: "The Public Transit & Electrified Rail Concordat",
    divergenceText: "In the late 1940s, a nationwide transit referendum outlawed urban highway construction through residential areas and invested all transportation fuel subsidies into a synchronized electric passenger and freight rail network.",
    divergenceEffects: "Eliminates suburban sprawl, fosters dense, walkable provincial centers, and tethers commercial life to scenic rail corridors.",
    societySummary: "A connected rail-hub society where compact, walkable towns surround bustling intermodal stations.",
    laws: "Strict urban growth boundaries, mandatory passenger rail connectivity for any town over five thousand residents, and public right-of-way dominance.",
    culture: "Walkable public concourses, communal dining dining cars, unhurried rail journeys, and quiet, automobile-free town centers.",
    institutions: "National Electrified Rail Authority, Intermodal Commerce Cooperatives, Urban Greenbelt Commissions, and Station Quarter Councils.",
    expectations: "Residents maintain compact urban dwellings, commute primarily via tram and bicycle, and respect shared civic quiet hours.",
    opportunity: "Geographically fluid across hundreds of linked townships, allowing practitioners to consult regionally without relocating.",
    technology: "High-efficiency electric locomotives, regenerative braking power grids, analog clockwork signaling backed by fiber telemetry, and precision machine shops.",
    science: "Advanced metallurgy, traction physics, atmospheric micro-forecasting, and kinetic energy recovery systems.",
    geography: "Vibrant high-density rail market towns surrounded by undisturbed agricultural belts and pristine national wilderness reserves.",
    history: "Eight decades of compact urban flourishing following the historic rejection of private automobile lobbying in 1947.",
    environment: "Negligible smog, quiet acoustic corridors in town centers, and thriving wildlife corridors across uninterrupted countryside.",
    howDiffersHeadline: "A civilization built around the communal rhythm of the electric rail line rather than private automobile isolation.",
    howDiffersParagraphs: [
      "While modern Earth surrendered vast geographic swaths to multi-lane highways, suburban sprawl, and parking asphalt, this society poured every public investment into clean, synchronized electric rail.",
      "Towns and small cities remain pedestrian sanctuaries where daily necessities, workshops, and civic halls sit within a ten-minute walk of the central station concourse.",
      "The frantic commute that exhausts contemporary Earth workers is replaced by quiet morning rail journeys through emerald valleys, turning travel into productive reflection and neighborly discussion."
    ],
    keyDifferences: [
      {
        title: "Complete Sprawl Prohibition",
        text: "Towns are bounded by permanent agricultural greenbelts, preventing the creeping concrete tracts common to Earth suburbs."
      },
      {
        title: "Intermodal Freight Supremacy",
        text: "Over ninety percent of cargo moves via electrified rail pods directly into municipal freight sheds rather than diesel highway trucks."
      },
      {
        title: "Acoustic Urban Sanctuaries",
        text: "City streets are free from automobile engine roar and toxic tailpipe emissions, returning public spaces to children and pedestrians."
      },
      {
        title: "Integrated Station Quarters",
        text: "Train stations serve as municipal centers housing libraries, public baths, produce markets, and communal workshops."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Specialist coordinating regional services along clean rail corridors",
        earth: "Commuter trapped in highway traffic or remote teleworker isolated at home",
        because: "Electric rail networks connect regional workshops directly into high-density municipal markets."
      },
      "Social status": {
        here: "Esteemed civic contributor known across several interconnected rail districts",
        earth: "Anonymous suburban commuter navigating corporate ladder dynamics",
        because: "Civic visibility spans regional rail hubs through regular face-to-face station gatherings."
      },
      "Financial circumstances": {
        here: "Low overhead living with zero private vehicle debt, fuel costs, or insurance burdens",
        earth: "Heavy monthly financial drain from car payments, suburban mortgages, and fuel inflation",
        because: "Public transit eliminates the massive personal financial burden of private automobile ownership."
      },
      "Relationships": {
        here: "Dynamic encounters on shared daily trains interwoven with close-knit neighborhood bonds",
        earth: "Fragmented social interactions limited by vehicle commuting and suburban isolation",
        because: "Shared public transport creates natural, recurring daily touchpoints with diverse community members."
      },
      "Freedoms": {
        here: "Effortless, low-cost regional mobility across thousands of miles of electrified track",
        earth: "Nominal car mobility often constrained by paralyzing traffic congestion and high fuel prices",
        because: "Transit is treated as a universal public utility funded through civic infrastructure reserves."
      },
      "Daily life": {
        here: "Walkable morning strolls to the depot, scenic rail rides, and quiet pedestrian avenues",
        earth: "Stressful bumper-to-bumper driving or endless sedentary zoom conferences",
        because: "Town design puts living quarters, workshops, and natural green spaces within human walking scale."
      },
      "Available technology": {
        here: "Rugged electric traction systems, precision mechanical relays, and robust local workshops",
        earth: "Complex consumer vehicles laden with proprietary sensors and expensive repair barriers",
        because: "Transportation engineering focuses on public efficiency, shared durability, and open maintenance."
      },
      "Future opportunities": {
        here: "Access to regional research institutes and specialized workshops without uprooting family life",
        earth: "Pressure to relocate to expensive mega-cities to pursue advanced career prospects",
        because: "High-speed rail links equalize economic opportunity across small towns and larger hubs."
      }
    }
  },
  {
    id: "d3",
    title: "The Watershed Commons & Cooperative Agriculture Accord",
    divergenceText: "Following severe regional dust storms in the 1930s, agricultural land and freshwater aquifers were declared non-tradable public trusts managed by watershed assemblies rather than speculative agribusiness corporations.",
    divergenceEffects: "Establishes regenerative polyculture, protects freshwater reserves, and guarantees every household access to organic food cooperatives.",
    societySummary: "An agro-ecological federation centered on basin stewardship, cooperative seed vaults, and regional food sovereignty.",
    laws: "Strict prohibitions against chemical aquifer contamination, communal land leasing based on soil health metrics, and water rights parity.",
    culture: "Seasonal harvest festivals, meticulous soil reverence, communal preservation kitchens, and pride in agricultural self-reliance.",
    institutions: "Watershed Drainage Assemblies, Heritage Seed Conservatories, Soil Health Magistrates, and Farmers' Credit Guilds.",
    expectations: "Residents contribute seasonal labor to harvest logistics, irrigation canal maintenance, and public orchard pruning.",
    opportunity: "Deeply meritocratic in agricultural science, water engineering, and ecological restoration, open to all willing hands.",
    technology: "Solar-powered drip irrigation, biological pest management, cold-storage root cellars, and modular electric farm machinery.",
    science: "Advanced soil biology, mycorrhizal network mapping, hydrology, and microclimate forecasting.",
    geography: "Lush terraced valley slopes, meandering restored river courses, clean mountain runoff lakes, and vibrant market squares.",
    history: "Nine decades of unbroken ecological regeneration after agricultural conglomerates were broken up during the Great Basin Drought.",
    environment: "Pristine drinking aquifers, rich black topsoil with increasing carbon depth, and diverse bird and pollinator populations.",
    howDiffersHeadline: "A society founded on the biological carrying capacity of the land rather than industrial extraction.",
    howDiffersParagraphs: [
      "Contemporary Earth permitted industrial agriculture to deplete topsoil, poison aquifers with synthetic runoff, and monopolize seed patents through multinational corporations.",
      "In this timeline, water and arable soil are legally protected as non-commodified commons; farming is conducted by generational cooperatives practicing sophisticated ecological polyculture.",
      "The food system prioritizes nutrient density, seasonal variety, and community resilience, ensuring that every family eats fresh, unadulterated produce grown within fifty miles of their doorstep."
    ],
    keyDifferences: [
      {
        title: "Non-Commodifiable Water Trusts",
        text: "Aquifers cannot be bought, sold, or bottled by private commercial entities for corporate profit."
      },
      {
        title: "Open Heritage Seed Repositories",
        text: "All crop genetics remain public property; patenting biological organisms carries severe civil penalties."
      },
      {
        title: "Soil Carbon Stewardship Standards",
        text: "Farmers receive direct civic dividend credits based on measurable increases in organic soil matter."
      },
      {
        title: "Regional Food Processing Hubs",
        text: "Every township operates municipal drying kilns, cold cellars, and canning facilities open to all growers."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Steward or technical specialist in an ecological watershed cooperative",
        earth: "Corporate agribusiness employee or disconnected urban service worker",
        because: "Land management is organized through democratic watershed assemblies rather than corporate farm trusts."
      },
      "Social status": {
        here: "Respected trustee of regional soil and water health with active community voice",
        earth: "Consumer at the mercy of global supply-chain shocks and supermarket markups",
        because: "Food sovereignty and ecological guardianship are celebrated as the highest civic callings."
      },
      "Financial circumstances": {
        here: "Extremely low grocery and water costs backed by cooperative harvest dividends",
        earth: "Vulnerable to escalating food inflation and corporate agricultural price fixing",
        because: "Non-profit food commons eliminate speculative middleman price spikes."
      },
      "Relationships": {
        here: "Cooperative ties forged through communal planting bees, canning rotations, and canal upkeep",
        earth: "Atomized urban lifestyle with minimal knowledge of who produces one's daily food",
        because: "Seasonal agricultural cycles involve the entire community in shared physical sustenance."
      },
      "Freedoms": {
        here: "Freedom from pesticide contamination, corporate food monopolies, and artificial scarcity",
        earth: "Endless brand variety in grocery aisles masking ultra-processed nutritional poverty",
        because: "Strict environmental statutes guarantee pristine water and wholesome, unadulterated nourishment."
      },
      "Daily life": {
        here: "Direct connection with seasonal harvests, fresh whole food preparation, and clean outdoor air",
        earth: "Convenience-driven meals consumed hurriedly between digital work appointments",
        because: "Daily schedules accommodate shared cooking and unhurried meals crafted from regional produce."
      },
      "Available technology": {
        here: "Solar irrigation arrays, biological diagnostic tools, and easily repairable electric tilling rigs",
        earth: "Complex proprietary computerized tractors locked by corporate intellectual property barriers",
        because: "Equipment is built on open standards to ensure immediate field repairs by local mechanics."
      },
      "Future opportunities": {
        here: "Leading regional ecological restoration projects and expanding heirloom seed diversity",
        earth: "Career trajectories exposed to environmental degradation and agricultural supply collapse",
        because: "Society continually invests in long-term biosphere health and regenerative land mastery."
      }
    }
  },
  {
    id: "d4",
    title: "The Polyglot Maritime League & Coastal Commons",
    divergenceText: "An eighteenth-century coastal federation preserved regional indigenous seafaring rights and established a self-policing maritime league that barred naval blockades and opened all coastal waterways to mutual trade.",
    divergenceEffects: "Creates a thriving archipelago culture, multilingual coastal harbor towns, and sustainable near-shore fisheries managed by community quotas.",
    societySummary: "A maritime trading federation of seafaring ports, timber wharves, and cooperative tidal fisheries.",
    laws: "Strict harbor sanctuary rights, seasonal fishery moratoriums enforced by local skippers, and mutual salvage protections.",
    culture: "Polyglot maritime patois, evening harbor concerts, open wharf markets, and universal swimming and seamanship education.",
    institutions: "The Coastal Pilots' League, Tidal Fishery Benches, Maritime Navigation Academies, and Port Mutual Canteens.",
    expectations: "Every citizen learns coastal piloting, navigation by tidal charts, and shares responsibility for lighthouse maintenance.",
    opportunity: "Open to anyone with seamanship skill, navigational courage, or mechanical fluency with marine diesel and timber craft.",
    technology: "Hybrid sail-electric cargo schooners, copper-clad wooden hulls, precise mechanical sextants, and low-frequency coastal radio beacons.",
    science: "Oceanography, tidal dynamics, marine biology, and celestial cartography.",
    geography: "Scenic peninsulas, protected sound anchorages, rocky archipelago outposts, and bustling waterfront stone quays.",
    history: "Two centuries of peaceful maritime exchange following the historic signing of the Harbor Sanctuary League.",
    environment: "Abundant coastal fisheries, restored kelp forests, clean tidal sounds, and thriving seal and seabird colonies.",
    howDiffersHeadline: "A civilization attuned to the breathing tides, where maritime commons are protected for all vessels.",
    howDiffersParagraphs: [
      "Unlike contemporary Earth, which depleted global oceans through commercial factory trawling and coastal petrochemical dumping, this society manages its waters through strict local skipper councils.",
      "The shoreline is legally protected as an inviolable public right-of-way; no private developer can claim an exclusive beach or wall off access to the working tides.",
      "Children grow up bilingual in maritime dialects, equally at home handling a tiller in heavy swell as they are analyzing tidal hydrodynamics in municipal academies."
    ],
    keyDifferences: [
      {
        title: "Factory Trawling Ban",
        text: "Industrial bottom trawlers are legally barred; all fishing is conducted with selective lines and sustainable traps."
      },
      {
        title: "Universal Shoreline Sanctuary",
        text: "The entire coastal rim is public trust property, guaranteeing unhindered passage for fishers, wanderers, and boats."
      },
      {
        title: "Hybrid Wind-Electric Shipping",
        text: "Cargo moves along the coast using automated wing-sail schooners with zero bunker-fuel sulfur pollution."
      },
      {
        title: "Decentralized Lighthouse Network",
        text: "Navigation aids and weather towers are operated cooperatively by harbor townships rather than distant military boards."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Navigator, maritime artisan, or port logistics lead in a self-governing harbor league",
        earth: "Containerized shipping specialist or landlocked corporate logistics analyst",
        because: "Maritime commerce is decentralized into independent, cooperative shipping guilds."
      },
      "Social status": {
        here: "Respected coastal skipper and active voter on the Tidal Harbor Bench",
        earth: "Anonymous corporate employee separated from the physical transit of goods",
        because: "Seamanship and harbor service carry immense cultural prestige across coastal towns."
      },
      "Financial circumstances": {
        here: "Shared ownership stakes in cargo schooners, catch dividends, and debt-free harbor moorage",
        earth: "Fixed salary subject to corporate shipping consolidation and global container tariffs",
        because: "Cooperative fleet ownership divides shipping profits directly among the working crew."
      },
      "Relationships": {
        here: "Unbreakable bonds formed through watch-standing in heavy seas and cooperative dockside repairs",
        earth: "Office relationships often constrained by competitive workplace politics and physical distance",
        because: "Navigational survival demands absolute mutual reliance and transparent communication."
      },
      "Freedoms": {
        here: "Unrestricted passage across coastal waters and full right of anchorage in every natural sound",
        earth: "Heavy maritime border restrictions, privatized shorelines, and commercial port fences",
        because: "The Maritime League guarantees universal sanctuary rights for all peaceful vessels."
      },
      "Daily life": {
        here: "Rhythms governed by tide tables, wind shifts, salt air, and lively waterfront market gatherings",
        earth: "Predictable clock-driven office hours under fluorescent lights away from natural weather",
        because: "Daily activity aligns with meteorological conditions rather than artificial corporate deadlines."
      },
      "Available technology": {
        here: "Advanced composite wing-sails, precision brass navigational gear, and clean electric auxiliaries",
        earth: "Massive diesel container ships burning dirty heavy fuel oils across distant oceans",
        because: "Naval engineering emphasizes environmental symbiosis and modular, clean electric propulsion."
      },
      "Future opportunities": {
        here: "Commanding independent coastal trade runs or founding new archipelago navigation academies",
        earth: "Corporate career paths vulnerable to shipping conglomerate buyouts and automated container ports",
        because: "The Maritime League actively supports young skippers establishing new cooperative trade routes."
      }
    }
  },
  {
    id: "d5",
    title: "The Universal Polymath Schooling & Apprenticeship System",
    divergenceText: "An educational revolution in the late nineteenth century abolished rote standardized testing, replacing it with compulsory dual schooling where all youth spend half their time in manual guilds and half in natural philosophy.",
    divergenceEffects: "Eliminates the artificial divide between intellectual and manual labor, creating a society of culturally literate, hands-on polymaths.",
    societySummary: "A culture of thoughtful makers, where doctors build furniture and machinists compose string quartets.",
    laws: "Statutory five-hour daily work caps for young apprentices, mandatory sabbatical years, and universal access to research ateliers.",
    culture: "Deep respect for both philosophical inquiry and physical craft; distaste for purely administrative credentialism.",
    institutions: "Civic Polymath Ateliers, Vocational Philosophy Circles, Free Guild Laboratories, and Municipal Book Benches.",
    expectations: "Every citizen maintains a secondary manual craft alongside their primary intellectual or social profession.",
    opportunity: "Fluid and self-directed, allowing individuals to change specializations across their lifetimes through guild sabbaticals.",
    technology: "Artisanal precision fabrication, open-source scientific apparatus, analog calculating machines, and durable printing presses.",
    science: "Integrative biology, mechanics, cognitive development, and empirical materials experimentation.",
    geography: "Townships centered on communal libraries and glass-roofed workshops surrounded by shared public orchards.",
    history: "A century of balanced cultural flourishing after pedagogical reforms dismantled rigid class-based school stratification.",
    environment: "Harmonious urban-rural balance with clean air, quiet town libraries, and sustainable local timber harvesting.",
    howDiffersHeadline: "A civilization where intellectual brilliance and physical craft are never forced apart.",
    howDiffersParagraphs: [
      "Modern Earth segregated society into desk-bound knowledge workers and alienated manual laborers, creating deep cultural resentment and psychological fatigue across both groups.",
      "In this reality, educational philosophy insists that genuine intelligence requires both manual competence and rigorous abstract thought; no child graduates without mastering both higher mathematics and fine carpentry.",
      "The result is a calm, versatile populace that repairs its own machines, writes its own local literature, and approaches civic problems with grounded common sense."
    ],
    keyDifferences: [
      {
        title: "Dual Intellectual-Manual Pedagogy",
        text: "All students split their education equally between seminar philosophy and physical guild workshops."
      },
      {
        title: "Universal Mid-Career Sabbaticals",
        text: "Every seven years, citizens are entitled to an accredited year of paid study or apprenticeship in an unrelated field."
      },
      {
        title: "Democratized Research Ateliers",
        text: "Advanced scientific equipment and fabrication tools are publicly housed in open municipal ateliers."
      },
      {
        title: "Absence of Credential Inflation",
        text: "Job roles are assigned through demonstrated workshop portfolios rather than expensive university degrees."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Dual-practice specialist balancing analytical research with tactile workshop execution",
        earth: "Hyper-specialized desk worker detached from the physical production of materials",
        because: "The schooling system trains all citizens to integrate manual craft with intellectual inquiry."
      },
      "Social status": {
        here: "Respected polymath valued equally for clear prose and flawless physical joinery",
        earth: "Evaluated primarily by corporate job title and educational degree prestige",
        because: "Society rejects class divisions between white-collar administrators and blue-collar makers."
      },
      "Financial circumstances": {
        here: "Debt-free education backed by public stipends and diversified craft income streams",
        earth: "Substantial student loan debt followed by reliance on a single corporate paycheck",
        because: "Education is publicly financed through municipal craft foundations rather than private debt."
      },
      "Relationships": {
        here: "Vibrant intellectual and creative circles spanning scientists, masons, and musicians",
        earth: "Social circles often restricted to colleagues within the same narrow corporate industry",
        because: "Shared community workshops bring people of diverse practical trades into daily collaboration."
      },
      "Freedoms": {
        here: "Freedom to pivot careers every seven years without financial destitution or loss of status",
        earth: "Locked into rigid career paths due to specialized resumes, mortgage debt, and health insurance",
        because: "Universal sabbatical laws protect workers exploring new disciplines throughout their adult lives."
      },
      "Daily life": {
        here: "Balanced days dividing time between quiet library contemplation and vigorous bench craft",
        earth: "Eight to ten continuous hours staring at backlit screens in sedentary office chairs",
        because: "Cultural norms dictate that mental exertion must be counterbalanced by physical creative labor."
      },
      "Available technology": {
        here: "Open-source laboratory apparatus, modular hand tools, and repairable mechanical engines",
        earth: "Proprietary software suites with monthly subscription models and opaque user algorithms",
        because: "Tools are designed for personal mastery, open experimentation, and lifelong durability."
      },
      "Future opportunities": {
        here: "Unlimited potential to master fresh crafts, publish independent research, or direct regional ateliers",
        earth: "Narrow promotion ladders subject to industry automation and corporate age bias",
        because: "Maturity and diverse cross-disciplinary experience are celebrated as core civic assets."
      }
    }
  },
  {
    id: "d6",
    title: "The Municipal Telegraph & Open Communication Post",
    divergenceText: "When electric telecommunication emerged, the postal service was constitutionally designated as the sole carrier of electronic messages, operating on a strict non-profit tariff with zero behavioral advertising or user surveillance.",
    divergenceEffects: "Preserves public epistolary culture, prevents addictive engagement algorithms, and treats digital bandwidth as a calm civic utility.",
    societySummary: "A quiet, thoughtful society where messages arrive on crisp stationery via pneumatic tube and dispatch rider.",
    laws: "Absolute privacy of the correspondence, strict prohibitions against commercial profiling, and universal flat-rate telegraph access.",
    culture: "Reflective letter-writing, community noticeboards, lively public debate halls, and tranquil, unhurried attention spans.",
    institutions: "The Civic Telegraphic Post, Municipal Pneumatic Dispatch, Public Reader Guilds, and Archival Printing Presses.",
    expectations: "Citizens compose thoughtful, concise written correspondence and respect daily quiet hours free from electronic bells.",
    opportunity: "Open to researchers, writers, and technical telegraphers across widespread provincial correspondence networks.",
    technology: "Brass duplex telegraph keys, paper-tape printing relays, pneumatic street tubes, and high-fidelity archival voice cylinders.",
    science: "Acoustics, electrical conduction, compression linguistics, and materials chemistry for paper preservation.",
    geography: "Towns adorned with copper telegraph spires, central postal rotundas, and quiet garden reading rooms.",
    history: "A century of tranquil civic discourse protected from commercial media monopolies and surveillance capitalism.",
    environment: "Acoustically peaceful cities free from siren-like notifications, with low electromagnetic emissions and clean air.",
    howDiffersHeadline: "A connected world that preserved human attention and dignity instead of monetizing outrage.",
    howDiffersParagraphs: [
      "Where modern Earth allowed private technology corporations to build addictive social media platforms that fragment attention and polarize communities, this society placed telecommunications under postal protection.",
      "Communication is treated as a sacred civic utility: messages are transmitted accurately, paid for with a nominal stamp, and delivered on paper without tracking, algorithmic sorting, or commercial interruption.",
      "As a consequence, public debate is deliberative and polite; citizens read deeply, write with literary precision, and gather in physical halls to settle disputes face to face."
    ],
    keyDifferences: [
      {
        title: "Constitutional Privacy of Correspondence",
        text: "Reading, profiling, or monetizing electronic messages carries immediate criminal penalties for postmasters."
      },
      {
        title: "Absence of Behavioral Advertising",
        text: "No platform is permitted to capture user attention for commercial sponsor auctions."
      },
      {
        title: "Pneumatic and Telegraphic Infrastructure",
        text: "Towns are connected via swift pneumatic dispatch tubes and reliable copper wire networks."
      },
      {
        title: "Protected Public Attention Commons",
        text: "Public spaces, parks, and dining halls are designated as noise-free sanctuaries where loud broadcasts are prohibited."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Specialist or administrator working with calm, deliberative postal communication channels",
        earth: "Worker drowning in endless unread emails, chat pings, and notification emergencies",
        because: "Communications are batch-delivered twice daily, preserving deep cognitive focus."
      },
      "Social status": {
        here: "Known for precise epistolary style, measured counsel, and dependable local presence",
        earth: "Chasing transient social media metrics, impressions, and viral internet visibility",
        because: "Reputation is built through long-form letters and physical civic assembly attendance."
      },
      "Financial circumstances": {
        here: "Zero expenditure on subscription software, continuous smartphone upgrades, or data plans",
        earth: "Continuous recurring charges for cloud storage, mobile connectivity, and streaming platforms",
        because: "Communication costs pennies per dispatch through the non-profit civic postal trust."
      },
      "Relationships": {
        here: "Cherished lifetime correspondence preserved in ribbon-tied packets alongside frequent in-person visits",
        earth: "Superficial digital connections scattered across multiple messaging apps with rare face-to-face depth",
        because: "Letters encourage honest, thoughtful vulnerability rather than performative digital posturing."
      },
      "Freedoms": {
        here: "Complete freedom from corporate digital surveillance, targeted manipulation, and attention theft",
        earth: "Continuous digital tracking, data profiling, and algorithmic nudging across all devices",
        because: "The constitution strictly outlaws corporate harvesting of personal communication habits."
      },
      "Daily life": {
        here: "Peaceful morning reading, uninterrupted work blocks, and quiet evenings around paper ledgers",
        earth: "Fragmented focus, continuous multi-tasking, and evening exhaustion from screen glare",
        because: "Workplaces are free from real-time digital surveillance and urgent chat notifications."
      },
      "Available technology": {
        here: "Precision mechanical telegraphs, pneumatic mail capsules, and letterpress printing equipment",
        earth: "Fragile smartphones with planned software obsolescence and closed operating systems",
        because: "Technology is designed for centurial mechanical endurance rather than rapid consumer churn."
      },
      "Future opportunities": {
        here: "Publishing authoritative treatises and expanding regional correspondence academies",
        earth: "Navigating volatile digital economy shifts and algorithmic displacement in the tech sector",
        because: "Society prioritizes lasting intellectual contribution over transient software trends."
      }
    }
  },
  {
    id: "d7",
    title: "The Urban Garden City & Zero-Waste Metabolism",
    divergenceText: "An influential urban architecture covenant drafted in the 1920s mandated that no building could exceed four stories, and every city block was required to dedicate half its footprint to communal food gardens and closed-loop composting.",
    divergenceEffects: "Prevents canyon-like urban alienation, ensures local food production within city borders, and eliminates municipal landfill waste.",
    societySummary: "A verdant, low-rise urban civilization where stone architecture embraces hanging gardens and community courtyards.",
    laws: "Mandatory daylight rights for all dwellings, closed-loop organic waste recycling, and maximum building height covenants.",
    culture: "Rooftop beekeeping, courtyard dinners, neighborhood gardening rotations, and a collective aesthetic of living greenery.",
    institutions: "Urban Metabolism Commissions, Courtyard Soil Trusts, Municipal Seed Benches, and Building Craft Guilds.",
    expectations: "Residents maintain their section of the courtyard garden and contribute food scraps to the block biodigester.",
    opportunity: "Vibrant within sustainable architecture, urban hydrology, permaculture, and decentralized renewable energy.",
    technology: "Rooftop rainwater cisterns, anaerobic biogas fermenters, solar greenhouses, and electric cargo tricycles.",
    science: "Urban microclimatology, closed-loop composting microbiology, acoustics, and passive solar thermodynamics.",
    geography: "Sunlit garden cities intertwined with forest corridors, free of concrete expressways or smog-filled canyons.",
    history: "A century of steady urban development where nature and human dwellings grew together in deliberate symbiosis.",
    environment: "Pristine urban air filled with floral scents, zero landfill runoff, and thriving populations of songbirds and honeybees.",
    howDiffersHeadline: "Cities that function as living, restorative ecosystems rather than concrete heat islands.",
    howDiffersParagraphs: [
      "Contemporary Earth built congested megalopolises of glass, steel, and asphalt that trap heat, generate mountains of plastic trash, and sever inhabitants from the natural world.",
      "In this society, urban design is governed by living metabolism: every block generates its own clean solar power, recycles all biological nutrients through shared courtyards, and yields rich seasonal harvests.",
      "Residents enjoy sunlit apartments looking out on fruit trees and flowering vines, living in close-knit communities where children play safely in car-free central gardens."
    ],
    keyDifferences: [
      {
        title: "Strict Daylight and Height Ordinances",
        text: "Buildings are capped at four stories to ensure warm sunlight touches every garden courtyard and residential window."
      },
      {
        title: "Zero Municipal Landfills",
        text: "All manufacturing uses biodegradable or fully recyclable materials; organic matter returns directly to courtyard soil."
      },
      {
        title: "Integrated Courtyard Agriculture",
        text: "City blocks grow up to forty percent of their own fresh produce in shared rooftop and terrace greenhouses."
      },
      {
        title: "Passive Solar Architecture",
        text: "Thick stone and terracotta construction utilizes natural thermal mass, eliminating noisy air conditioning units."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Specialist operating in a low-rise, daylight-flooded urban workshop with direct garden access",
        earth: "Office worker sequestered in an artificial fluorescent cubicle high above noisy city streets",
        because: "Urban planning requires all workspaces to integrate natural light and living botanical gardens."
      },
      "Social status": {
        here: "Respected neighborhood courtyard steward and active contributor to urban ecology",
        earth: "Isolated apartment renter with no stake in neighborhood governance or public grounds",
        because: "Every resident shares democratic ownership and management of their block's communal garden."
      },
      "Financial circumstances": {
        here: "Low cost of living assisted by free courtyard produce, shared solar energy, and cooperative housing",
        earth: "High urban rent burdens, escalating utility bills, and expensive restaurant food costs",
        because: "Cooperative housing models prevent speculative land landlordism and lower basic living expenses."
      },
      "Relationships": {
        here: "Warm daily encounters with courtyard neighbors while tending vegetables or sharing evening dinners",
        earth: "Passing strangers in elevator lobbies with minimal neighborhood community bonds",
        because: "Shared outdoor courtyards naturally encourage daily spontaneous social interaction."
      },
      "Freedoms": {
        here: "Abundant access to clean open green spaces, fresh air, and edible community landscapes",
        earth: "Restricted to small private apartments or crowded public parks far from home",
        because: "Every citizen lives within twenty steps of a thriving community garden and public orchard."
      },
      "Daily life": {
        here: "Morning harvesting, work in naturally ventilated ateliers, and leisurely dinners on the terrace",
        earth: "Rushed morning subways, artificial climate control, and evenings spent on private couches",
        because: "The urban fabric prioritizes sensory well-being, fresh air, and connection to plant cycles."
      },
      "Available technology": {
        here: "Anaerobic biodigesters, rainwater filtration urns, and silent electric utility tricycles",
        earth: "Complex municipal waste incinerators, diesel garbage fleets, and disposable packaging systems",
        because: "Technology is designed for closed-loop, circular biological cycles without toxic byproducts."
      },
      "Future opportunities": {
        here: "Leading city-wide ecological retrofits and designing innovative low-carbon civic structures",
        earth: "Vulnerable to urban climate vulnerability, rising heatwaves, and aging municipal infrastructure",
        because: "Cities continuously invest in ecological resilience and citizen-managed urban forestry."
      }
    }
  },
  {
    id: "d8",
    title: "The Geothermal Thermal Grid & Bathhouse Commons",
    divergenceText: "A widespread public works initiative following the discovery of shallow volcanic aquifers in the nineteenth century piped geothermal steam and mineral water through every municipality, establishing communal public baths as the center of civic life.",
    divergenceEffects: "Eliminates winter heating poverty, provides universal free hot water, and cements public bathing as an egalitarian democratic ritual.",
    societySummary: "A clean, warm civilization anchored by steaming stone public baths, district thermal conduits, and heated winter streets.",
    laws: "Universal free hot water access, strict environmental protection of volcanic vents, and sanctuary laws within municipal bathhouses.",
    culture: "Daily evening bathhouse gatherings, unhurried political consensus in hot pools, and absence of physical self-consciousness.",
    institutions: "District Geothermal Boards, Bathhouse Custodian Guilds, Mineral Water Assayers, and Thermal Engineering Syndicates.",
    expectations: "Residents visit the municipal bathhouse several times a week to bathe, converse, and participate in informal ward voting.",
    opportunity: "Broad opportunities across geothermal drilling, thermodynamics, mineral therapy, and municipal hydraulic engineering.",
    technology: "Heavy bronze steam valves, insulated basalt conduits, Stirling cycle thermal generators, and mineral filtration vats.",
    science: "Geophysics, vulcanology, hydrology, and hydrotherapeutic medicine.",
    geography: "Mist-wreathed mountain valley towns with terracotta roofs, bubbling public fountains, and thermal greenhouse complexes.",
    history: "A century and a half of warm civic solidarity after public thermal trusts replaced private coal heating monopolies.",
    environment: "Crystal clear winter air free from coal or wood smoke, with thriving hot-spring wetlands and winter garden terraces.",
    howDiffersHeadline: "A society warmed from beneath the earth, where citizens deliberate together in the calming steam.",
    howDiffersParagraphs: [
      "While contemporary Earth burned fossil fuels to warm individual homes, generating massive greenhouse emissions and fuel poverty, this civilization tapped the planet's internal heat directly through public geothermal conduits.",
      "Heating is a universal public birthright; snow melts automatically on main municipal paths, and homes are warmed by circulating hot water without burning a single piece of wood or coal.",
      "The bathhouse serves as the ultimate social equalizer: executives, craftspeople, and students sit together in steaming mineral waters without suits or smartphones, resolving disputes with warmth and candor."
    ],
    keyDifferences: [
      {
        title: "Universal Free Thermal Energy",
        text: "Municipal steam grids heat all homes, workshops, and civic halls without individual billing meters."
      },
      {
        title: "Egalitarian Bathhouse Governance",
        text: "Ward assemblies meet in public bathhouse rotundas, where naked equality strips away false social pretenses."
      },
      {
        title: "Year-Round Geothermal Greenhouses",
        text: "Surplus steam feeds municipal glasshouses, yielding fresh citrus, herbs, and vegetables through freezing winters."
      },
      {
        title: "Absence of Urban Winter Smog",
        text: "Cities produce zero heating smoke, keeping winter skies sapphire blue and air crisp and pristine."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Specialist operating within warm, steam-heated district ateliers with steady seasonal rhythms",
        earth: "Worker facing volatile winter heating utility bills and high-stress corporate deadlines",
        because: "Workplaces are warmed by municipal geothermal grids, keeping operational costs exceptionally low."
      },
      "Social status": {
        here: "Respected community member sharing regular thermal baths with town leaders and apprentices alike",
        earth: "Isolated in private domestic spaces with social standing framed by outward material displays",
        because: "Public bathhouse culture strips away outward material clothing, emphasizing personal integrity."
      },
      "Financial circumstances": {
        here: "Completely insulated from winter energy bills, heating inflation, or fossil fuel price spikes",
        earth: "Burdened by seasonal utility price increases and volatile home energy maintenance costs",
        because: "Geothermal heat is treated as an unmetered, non-commodified municipal birthright."
      },
      "Relationships": {
        here: "Intimate, relaxed friendships nurtured through weekly hours conversing in steaming mineral pools",
        earth: "Social gatherings rushed between busy schedules, often mediated by noisy bars or digital chats",
        because: "The bathhouse provides a tranquil, phone-free physical sanctuary for deep conversation."
      },
      "Freedoms": {
        here: "Freedom from winter cold, fuel poverty, and the physical isolation of harsh seasonal freezes",
        earth: "Constrained by energy bills and limited public gathering spaces during winter months",
        because: "Heated streets and free public bathhouses ensure community life flourishes year-round."
      },
      "Daily life": {
        here: "Unwinding every evening in mineral waters surrounded by friends, followed by restful deep sleep",
        earth: "Hasty private showers, evening television consumption, and persistent physical muscular tension",
        because: "The communal bathhouse is a universal daily ritual that dissolves physical stress and fatigue."
      },
      "Available technology": {
        here: "Heavy-duty bronze hydraulic valves, basalt heat exchangers, and Stirling thermal motors",
        earth: "Complex, fragile home furnaces requiring continuous maintenance and proprietary digital thermostats",
        because: "Geothermal infrastructure is engineered from durable stone and bronze built to last a century."
      },
      "Future opportunities": {
        here: "Leading geothermal basin expansion and designing innovative thermal aquaculture facilities",
        earth: "Vulnerable to broader economic and climate shocks from global fossil fuel volatility",
        because: "The local economy is permanently anchored to the inexhaustible heat of the living earth."
      }
    }
  },
  {
    id: "d9",
    title: "The Precision Watchmakers' Guild & Clockwork Standardization",
    divergenceText: "An eighteenth-century decree established mechanical horology as the premier state science, creating a culture obsessed with precision hand-finishing, mechanical micro-engineering, and the ethical valuation of human time.",
    divergenceEffects: "Prevents the disposable electronic gadget boom in favor of exquisite, repairable clockwork and mechanical computation.",
    societySummary: "A meticulous mechanical civilization of ticking brass gears, jewel bearings, and enduring craftsmanship.",
    laws: "Strict penalties for shoddy material casting, mandatory twenty-year apprenticeships for master horologists, and time-conservation laws.",
    culture: "Punctuality without anxiety, deep appreciation for micro-mechanical ingenuity, and reverence for generational heirlooms.",
    institutions: "The High Horological Bench, Master Clockmakers' Guild, Chronometric Assayers, and Precision Apprenticeship Lodges.",
    expectations: "Citizens care for their mechanical pocket chronometers and practice precision, patience, and honesty in all dealings.",
    opportunity: "Exceptional for minds with high spatial reasoning, manual dexterity, and mathematical patience.",
    technology: "Bimetallic temperature-compensated balance wheels, mechanical analog difference engines, and jewel-bearing relays.",
    science: "Classical kinematics, precision metallurgy, celestial chronometry, and vibrational physics.",
    geography: "Alpine river valleys with roaring water-powered grinding mills, stone clock towers, and spotless wooden ateliers.",
    history: "Two centuries of peaceful refinement in micro-mechanics, untouched by the chaotic disruptions of digital obsolescence.",
    environment: "Pristine mountain streams powering waterwheels, with zero toxic electronic e-waste or lithium contamination.",
    howDiffersHeadline: "A world of jewel bearings and spring-steel gears where every machine is built to outlast its maker.",
    howDiffersParagraphs: [
      "Contemporary Earth became addicted to cheap, disposable plastic electronics that break in two years and poison global water supplies with heavy metal e-waste.",
      "In this reality, technology advanced through micro-mechanical genius: calculations are performed by gleaming brass difference engines, and daily timekeeping is kept by masterwork pocket chronometers handed down across four generations.",
      "Because every machine must be assembled by hand and lubricated with pure botanical oils, people treat manufactured goods with reverence, respecting the human hours embedded in their creation."
    ],
    keyDifferences: [
      {
        title: "Perpetual Mechanical Endurance",
        text: "Machines are crafted from marine bronze, tempered steel, and synthetic sapphire, designed to run for centuries."
      },
      {
        title: "Zero Toxic Electronic Waste",
        text: "Absence of toxic printed circuit boards and hazardous lithium cells preserves pure mountain watersheds."
      },
      {
        title: "Mechanical Calculating Engines",
        text: "Complex logistical and scientific calculations are executed by audible, transparent mechanical gear trains."
      },
      {
        title: "Reverence for Human Labor",
        text: "Goods carry the engraved signature of their artisan, cementing direct personal accountability for quality."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Master horologist, precision machinist, or analytical engineer working with durable physical gear trains",
        earth: "Software technician or consumer electronics marketer managing rapid obsolescence cycles",
        because: "Industry revolves around enduring micro-mechanics rather than disposable electronic gadgets."
      },
      "Social status": {
        here: "Venerated master craftsman whose mark on a chronometer guarantees regional esteem",
        earth: "Corporate employee whose skill set risks obsolescence every three to five years",
        because: "Exacting manual precision and deep mechanical knowledge carry lifelong cultural honor."
      },
      "Financial circumstances": {
        here: "Wealth held in tangible heirloom tools, precious metal reserves, and guild equity",
        earth: "Financial standing tied to fluctuating paper stock markets and depreciating consumer electronics",
        because: "Physical assets are built from precious, durable materials that never lose intrinsic utility."
      },
      "Relationships": {
        here: "Deep generational bonds between masters, journeymen, and apprentices working side by side for decades",
        earth: "High workplace turnover with professional contacts changing every few years",
        because: "Apprenticeship traditions cultivate deep lifetime loyalties and mutual pedagogical care."
      },
      "Freedoms": {
        here: "Independence born from total mastery over the tools and instruments of one's daily livelihood",
        earth: "Dependence on proprietary software ecosystems and remote cloud servers for daily functioning",
        because: "Every tool can be completely disassembled, understood, and repaired by its user."
      },
      "Daily life": {
        here: "Calm, focused hours at a sunlit bench accompanied by the rhythmic ticking of master escapements",
        earth: "Scattered attention divided between multiple glowing screens and automated notifications",
        because: "The acoustic environment is rhythmic and mechanical rather than frantic and digital."
      },
      "Available technology": {
        here: "Exquisite mechanical calculators, ruby-bearing chronometers, and water-driven gear lathes",
        earth: "Sealed black-box smartphones with unrepairable microchips and proprietary batteries",
        because: "Technology is completely transparent: every gear, lever, and spring can be physically observed."
      },
      "Future opportunities": {
        here: "Creating an undisputed masterwork chronometer that will tick in the municipal tower for two centuries",
        earth: "Chasing temporary promotions in volatile tech industries vulnerable to restructuring",
        because: "Society evaluates achievement through generational endurance rather than quarterly speed."
      }
    }
  },
  {
    id: "d10",
    title: "The Cooperative Alpine Foresters & Timber Guild",
    divergenceText: "An early forestry charter prohibited clear-cut logging and mandated that every tree felled must be selected by a unanimous council of village elders and replaced with three native saplings, establishing a sustainable timber economy.",
    divergenceEffects: "Preserves ancient alpine old-growth, nurtures world-class heavy timber carpentry, and ensures pristine mountain watersheds.",
    societySummary: "A mountain woodland federation of timber architects, forest ecologists, and high-altitude river settlements.",
    laws: "Strict continuous-cover forestry mandates, community woodlot allotments, and universal avalanche buffer protections.",
    culture: "Reverence for slow-growing cedar and larch, woodcarving traditions, alpine horn signals, and shared winter firewood banks.",
    institutions: "The Alpine Forest Council, Master Woodwrights' Lodge, Watershed Snow Wardens, and Timber Rafting Syndicates.",
    expectations: "Residents spend seasonal days planting native seedlings and clearing deadfall to protect the valley from wildfire.",
    opportunity: "Outstanding for ecological forestry, structural timber engineering, watershed hydrology, and botanical research.",
    technology: "Heavy water-powered timber saws, cable-skidding aerial tramways, biological tree health sensors, and wooden suspension bridges.",
    science: "Silviculture, dendrochronology, mountain meteorology, and wood structural mechanics.",
    geography: "Towering pine-crested peaks, emerald glacial lakes, roaring mountain flumes, and robust timber chalets.",
    history: "Two centuries of stable alpine prosperity under the unbroken stewardship of the Community Forest Concordat.",
    environment: "Resilient old-growth canopy with healthy populations of lynx, chamois, golden eagles, and pure glacial streams.",
    howDiffersHeadline: "A civilization sheltered beneath deep alpine forests that have never known clear-cut destruction.",
    howDiffersParagraphs: [
      "Where contemporary Earth flattened primary forests for cheap paper pulp and cattle ranching, creating landslides and carbon crises, this civilization treated its forests as living cathedrals.",
      "Every beam in every building is harvested with reverence: woodwrights know which ridge their timber grew on, seasoning larch and spruce for a decade until it achieves petrified endurance.",
      "The result is a landscape of profound beauty, where human settlements blend into towering green canopies that naturally shield villages from snow avalanches and soil erosion."
    ],
    keyDifferences: [
      {
        title: "Selective Single-Tree Forestry",
        text: "Clear-cutting is strictly outlawed; timber is harvested tree-by-tree without disrupting the canopy."
      },
      {
        title: "Generational Wood Seasoning",
        text: "Building timbers are air-seasoned in municipal drying sheds for ten years before structural use."
      },
      {
        title: "Natural Avalanche Shields",
        text: "Forest slopes above villages are permanently protected as inviolable community protection zones."
      },
      {
        title: "Universal Firewood Reserves",
        text: "Every resident receives clean split firewood from managed deadfall through the civic heating trust."
      }
    ],
    earthDimensionBecause: {
      "Occupation": {
        here: "Forest steward, master timber architect, or hydrological surveyor in an alpine cooperative",
        earth: "Worker in industrial corporate construction or real estate speculation",
        because: "Forestry and architecture are managed as long-term ecological crafts rather than commodity extraction."
      },
      "Social status": {
        here: "Respected woodland warden entrusted with selecting trees for the next generation's civic halls",
        earth: "Subcontractor evaluated by low-bid budgets and rushed construction timelines",
        because: "Timber masters are honored as the architects of community safety and generational shelter."
      },
      "Financial circumstances": {
        here: "Debt-free home built from family timber allotments, with zero heating fuel expenses",
        earth: "Massive thirty-year mortgage on suburban tract housing built with cheap drywall and studs",
        because: "Community wood reserves provide durable, high-integrity building materials at cost."
      },
      "Relationships": {
        here: "Lifelong kinship forged during timber rafting, sapling planting bees, and alpine winter shelter",
        earth: "Transitory professional relationships with high mobility and weak neighborhood ties",
        because: "Mountain topography and cooperative labor demand absolute mutual solidarity."
      },
      "Freedoms": {
        here: "Unlimited freedom to roam vast old-growth ridges, camp beneath ancient trees, and breathe pure mountain air",
        earth: "Restricted by private land boundaries, deforested industrial zones, and suburban fencing",
        because: "All mountain forests remain accessible as shared community walking commons."
      },
      "Daily life": {
        here: "Early mornings in fragrant pine air, physical work with hand-axes and drawknives, and warm fireside evenings",
        earth: "Sedentary desk schedules punctuated by highway commutes and artificial indoor air",
        because: "Daily activity is integrated with mountain geography, physical exertion, and natural materials."
      },
      "Available technology": {
        here: "Gravity cable tramways, water-powered band saws, and heavy timber joinery without steel fasteners",
        earth: "Cheap manufactured particle boards laden with formaldehyde glues and synthetic resins",
        because: "Building technology maximizes natural wood strength without toxic chemical additives."
      },
      "Future opportunities": {
        here: "Designing timber structures that will stand for three centuries, or nurturing newly planted groves",
        earth: "Navigating unpredictable boom-bust cycles in speculative real estate markets",
        because: "The forest guild guarantees long-term stability across century-long planting rotations."
      }
    }
  }
];

export const DIVERGENT_ROUTINE_TEMPLATES: readonly string[][] = [
  [
    "05:45 — Kindle the workshop hearth and check the barometric brass gauges on the porch.",
    "07:15 — Review the day's repair log over black tea, fresh bread, and orchard preserves.",
    "09:30 — Hands-on diagnostic inspection and tooling at the central workbench.",
    "12:30 — Midday repast and consultation with visiting apprentices at the long timber table.",
    "15:00 — Field walk across the municipal district to survey current public works projects.",
    "18:45 — Quiet evening journal entry, tool stropping, and listening to the evening radio."
  ],
  [
    "06:00 — Early walk along the canal towpath while the river mist still hugs the stone arches.",
    "07:30 — Greet junior practitioners at the guild gate and allocate morning tasks.",
    "10:00 — Demanding technical execution requiring fine measurement and calibrated hand tools.",
    "12:45 — Simple meal of vegetable broth and sharp cheese with neighboring artisans.",
    "14:30 — Formal meeting with the district council delegate to review quarterly material reserves.",
    "19:00 — Return home for a warm dinner, classical cello music, and correspondence with distant peers."
  ],
  [
    "05:30 — Check the overnight telemetry readings at the water gate station before dawn light.",
    "07:00 — Quiet breakfast of porridge and dried fruit while reviewing regional guild circulars.",
    "08:45 — Direct supervision of apprentice safety drills and heavy casting calibrations.",
    "12:15 — Communal lunch in the courtyard garden discussing seasonal weather projections.",
    "14:45 — Complex problem-solving session troubleshooting unexpected material stress fractures.",
    "18:30 — Evening stroll through the market square, picking up fresh provisions for the household."
  ],
  [
    "06:15 — Brew bitter chicory coffee and inspect the drying timber stocks in the upper loft.",
    "07:45 — Open the street-facing atelier shutters and sweep the stone flagstones.",
    "09:45 — Precise manual drafting on linen paper, calculating stress vectors for the new bridge arch.",
    "13:00 — Bread, cider, and lively debate on civic water quotas with visiting craft leads.",
    "15:30 — Practical bench execution, shaping seasoned wood with spokeshave and chisel.",
    "19:15 — Lantern-lit reading of historical engineering journals by the tile stove."
  ]
];
